import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useCallback } from 'react';

export type MeritEnginePage = 
  | 'dashboard' 
  | 'load-price' 
  | 'resources' 
  | 'merit-order-dispatch' 
  | 'bid' 
  | 'simulation-generation'
  | 'archive';

export interface MeritEngineNavigationParams {
  page: MeritEnginePage;
  date?: string;
  session?: string;
  tab?: string;
  [key: string]: string | undefined;
}

export const useMeritEngineNavigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const getCurrentPage = useCallback((): MeritEnginePage | null => {
    const page = searchParams.get('tab');
    return page as MeritEnginePage || null;
  }, [searchParams]);

  const getCurrentParams = useCallback((): MeritEngineNavigationParams => {
    const params: MeritEngineNavigationParams = {
      page: getCurrentPage() || 'dashboard'
    };
    
    // Add all search parameters
    searchParams.forEach((value, key) => {
      if (key !== 'tab') {
        params[key] = value;
      }
    });
    
    return params;
  }, [searchParams, getCurrentPage]);

  const navigateToPage = useCallback((
    page: MeritEnginePage, 
    additionalParams: Record<string, string> = {}
  ) => {
    const currentParams = new URLSearchParams(searchParams);
    
    // Set the page parameter
    currentParams.set('tab', page);
    
    // Add or update additional parameters
    Object.entries(additionalParams).forEach(([key, value]) => {
      if (value) {
        currentParams.set(key, value);
      } else {
        currentParams.delete(key);
      }
    });
    
    // Navigate to the same route with updated query parameters
    navigate(`${location.pathname}?${currentParams.toString()}`, { replace: false });
  }, [navigate, location.pathname, searchParams]);

  const updateParams = useCallback((params: Record<string, string>) => {
    const currentParams = new URLSearchParams(searchParams);
    
    Object.entries(params).forEach(([key, value]) => {
      if (value) {
        currentParams.set(key, value);
      } else {
        currentParams.delete(key);
      }
    });
    
    navigate(`${location.pathname}?${currentParams.toString()}`, { replace: true });
  }, [navigate, location.pathname, searchParams]);

  const goToPreviousStage = useCallback(async (getModSession: () => Promise<any>) => {
    try {
      const res = await getModSession();
      if (res) {
        if (res.stage === "LOAD") {
          navigateToPage('load-price');
        } else if (res.stage === "GENERATION") {
          navigateToPage('resources');
        } else if (res.stage === "MOD") {
          navigateToPage('merit-order-dispatch');
        } else if (res.stage === "BID") {
          navigateToPage('bid');
        }
      } else {
        alert("Error in continue to previous Session");
      }
    } catch (error) {
      console.error("Error getting previous session:", error);
      alert("Error in continue to previous Session");
    }
  }, [navigateToPage]);

  const goToDashboard = useCallback(() => {
    navigateToPage('dashboard');
  }, [navigateToPage]);

  return {
    getCurrentPage,
    getCurrentParams,
    navigateToPage,
    updateParams,
    goToPreviousStage,
    goToDashboard,
    currentPage: getCurrentPage(),
    currentParams: getCurrentParams()
  };
};
